import { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AIGuideService from "./AIGuide.service";
import { CATEGORY_LIMITS, countWords } from "./AIGuide.types";
import { PLACEHOLDER_CONTENT } from "./AIGuide.dummyData";
import type { AIGuideCategory, AIGuideDocument } from "./AIGuide.types";

function WordCounter({ value, max }: { value: number; max: number }) {
  const over = value > max;
  return (
    <span className={`text-[11px] font-bold ${over ? "text-red-600" : "text-muted-foreground"}`}>
      {value}/{max} words
    </span>
  );
}

function formatDate(ms: number | null) {
  if (!ms) return null;
  return new Date(ms).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function CategoryEditor({ category }: { category: AIGuideCategory }) {
  const service = useRef(new AIGuideService());
  const limits = CATEGORY_LIMITS[ category ];

  const [ savedDoc, setSavedDoc ] = useState<AIGuideDocument | null>(null);
  const [ content, setContent ] = useState("");
  const [ isActive, setIsActive ] = useState(true);
  const [ loading, setLoading ] = useState(false);
  const [ saving, setSaving ] = useState(false);
  const [ error, setError ] = useState<string | null>(null);

  const fetchDoc = async () => {
    setLoading(true);
    try {
      const result = await service.current.fetchAllDocuments();
      if (result.success) {
        const doc = result.byCategory[ category ];
        setSavedDoc(doc);
        setContent(doc.content);
        setIsActive(doc.isActive);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchDoc(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const wordCount = countWords(content);
  const overLimit = wordCount > limits.maxWords;
  const dirty = savedDoc ? (content !== savedDoc.content || isActive !== savedDoc.isActive) : false;

  const handleSave = async () => {
    if (overLimit) return;
    setSaving(true);
    setError(null);
    try {
      const result = await service.current.saveDocument(category, content.trim(), isActive);
      if (result.success) {
        setSavedDoc(result.document);
        setContent(result.document.content);
        setIsActive(result.document.isActive);
      } else {
        setError(result.message || "Failed to save");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-border/60 shadow-sm overflow-hidden bg-card">
      <CardHeader className="border-b border-border/40 bg-muted/5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base font-semibold">{limits.label}</CardTitle>
            <CardDescription className="text-sm">
              One shared document — this is exactly what gets sent to the AI as context on every question, in full.
              {savedDoc?.updatedAt && ` Last updated ${formatDate(savedDoc.updatedAt)}.`}
            </CardDescription>
          </div>
          <Button onClick={handleSave} disabled={saving || loading || overLimit || !dirty} className="gap-2 shrink-0">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-4">
        {loading ? (
          <div className="h-64 rounded-md bg-muted animate-pulse" />
        ) : (
          <>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label className="text-[11px] font-bold text-muted-foreground">Content</Label>
                <WordCounter value={wordCount} max={limits.maxWords} />
              </div>
              <Textarea
                rows={14}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder={PLACEHOLDER_CONTENT[ category ]}
                className={overLimit ? "border-red-500 focus-visible:border-red-500" : undefined}
              />
              {overLimit && (
                <p className="text-[11px] font-semibold text-red-600">
                  Over the {limits.maxWords}-word limit by {wordCount - limits.maxWords} words — trim it before saving.
                </p>
              )}
            </div>

            <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 p-4">
              <div>
                <p className="text-sm font-bold text-primary">Active</p>
                <p className="text-[11px] text-primary/70">When off, this section is kept but never sent to the AI.</p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>

            {error && <p className="text-xs font-semibold text-red-600">{error}</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function AIGuidePage() {
  return (
    <div className="min-h-screen bg-[oklch(0.97_0.01_90)] p-6 md:p-8 text-foreground">
      <div className="mb-8">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" /> AI-Guide
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Institute-specific knowledge the mobile app's AI assistant uses to answer student & faculty questions accurately.
        </p>
      </div>

      <Tabs defaultValue="news_updates">
        <TabsList>
          <TabsTrigger value="news_updates">News & Updates</TabsTrigger>
          <TabsTrigger value="general">General Information</TabsTrigger>
        </TabsList>
        <TabsContent value="news_updates">
          <CategoryEditor category="news_updates" />
        </TabsContent>
        <TabsContent value="general">
          <CategoryEditor category="general" />
        </TabsContent>
      </Tabs>
    </div>
  );
}
